/**
 * Alarm evaluation for the Live Monitor — driven entirely by alarmConfig.
 *
 * Three kinds of rule:
 *   limit      a vital or battery value beyond a threshold (HR > 150 …)
 *   event      a rhythm call declared by the analysis stage via vitals.events
 *              (VT, ASYSTOLE, PAUSE, AFIB …). An event with NO matching rule
 *              still surfaces as a medium-priority alarm, so a new event type
 *              from the analysis is never silently dropped.
 *   technical  lead off / device off / disconnected — the last two are decided
 *              by the card (they are about missing data); this module only
 *              turns them into alarms via techAlert().
 *
 * Every rule has an ALARM CONDITION DELAY (delaySec): the condition must hold
 * continuously that long before the alarm is declared. The `tracker` object
 * (one per card) remembers when each condition began.
 */
import { getAlarmConfig, PRIORITIES, TECH_CONDITIONS, normEvent } from './alarmConfig';

export const SEVERITY = { critical: 0, warning: 1, low: 2 };

function toAlert(rule, extra) {
  const P = PRIORITIES[rule.priority] || PRIORITIES.medium;
  return Object.assign({
    key: rule.id,
    label: rule.label,
    priority: rule.priority,
    severity: P.severity,          // 'critical' | 'warning' | 'low' — drives card colour/status
    mark: P.mark,                  // !!! / !! / !  (IEC 60601-1-8 priority marking)
    sound: rule.sound,
    repeatSec: rule.repeatSec,
    maxSoundSec: rule.maxSoundSec,
    audible: rule.audible !== false,   // false = muted for everyone in Alarm settings
    param: rule.param || null,
  }, extra || {});
}

// An alarm that is on stays on until its condition has been absent this long —
// otherwise a value hovering at a limit re-raises (and re-SOUNDS) on every dip.
// Same value as the server record (web_server/lib/liveAlarmEngine.js).
export const CLEAR_HOLD_MS = 5000;

/**
 * Alarm-condition state machine for one rule: OFF → (condition held for the
 * rule's delay) → ON → (condition absent for holdMs) → OFF.
 */
function held(tracker, rule, cond, now, holdMs) {
  let s = tracker[rule.id];
  if (!s) { if (!cond) return false; s = tracker[rule.id] = { since: null, on: false, offSince: null }; }
  if (cond) {
    s.offSince = null;
    if (s.since == null) s.since = now;
    if (!s.on && now - s.since >= (rule.delaySec || 0) * 1000) s.on = true;
  } else if (s.on) {
    if (s.offSince == null) s.offSince = now;
    if (now - s.offSince >= (holdMs || 0)) { delete tracker[rule.id]; return false; }
  } else { delete tracker[rule.id]; return false; }
  return s.on;
}

/** Alarm for a connection condition the card has detected, or null if disabled. */
export function techAlert(id) {
  const rule = getAlarmConfig().rules.find(r => r.kind === 'technical' && r.tech === id);
  if (!rule || !rule.enabled) return null;
  return toAlert(rule, { conn: true, text: (TECH_CONDITIONS[id] || {}).text || rule.label });
}

/**
 * @param vitals  latest vitals for this batch
 * @param opts    { tracker, now } — tracker is a plain object owned by the card
 * @returns active alarms, highest priority first
 */
export function evaluateAlerts(vitals, opts) {
  if (!vitals) return [];
  opts = opts || {};
  const tracker = opts.tracker || {};
  const now = opts.now || Date.now();
  const rules = getAlarmConfig().rules.filter(r => r.enabled);
  const active = [];

  // ── limits: for each (parameter, direction) keep only the highest priority
  const bestLimit = {};
  rules.forEach(rule => {
    if (rule.kind !== 'limit') return;
    const v = vitals[rule.param];
    const valid = v != null && !isNaN(v);
    const cond = valid && (rule.op === 'lt' ? v < rule.value : v > rule.value);
    if (!held(tracker, rule, cond, now, CLEAR_HOLD_MS)) return;
    const slot = rule.param + ':' + rule.op;
    const prev = bestLimit[slot];
    if (!prev || PRIORITIES[rule.priority].rank < PRIORITIES[prev.priority].rank) bestLimit[slot] = rule;
  });
  Object.keys(bestLimit).forEach(slot => active.push(toAlert(bestLimit[slot])));

  // ── technical: lead off (device off / disconnected come from the card)
  const lead = rules.find(r => r.kind === 'technical' && r.tech === 'lead_off');
  if (lead && held(tracker, lead, !!vitals.leadOff, now, CLEAR_HOLD_MS)) {
    active.push(toAlert(lead, { conn: true, text: TECH_CONDITIONS.lead_off.text }));
  } else if (!lead) {
    delete tracker.lead_off;
  }

  // ── arrhythmia events
  const events = vitals.events || (vitals.event ? [vitals.event] : []);
  const present = {};
  events.forEach(e => { const n = normEvent(e); if (n) present[n] = String(e); });
  const eventRules = getAlarmConfig().rules.filter(r => r.kind === 'event');
  eventRules.forEach(rule => {
    if (!rule.enabled) { delete tracker[rule.id]; return; }
    if (held(tracker, rule, !!present[rule.event], now, 0)) active.push(toAlert(rule));   // the analysis decides when a rhythm ends
  });
  Object.keys(present).forEach(code => {
    // Unknown to the configuration (not merely disabled) → still alarm.
    if (eventRules.some(r => r.event === code)) return;
    active.push(toAlert({
      id: 'ev_' + code.toLowerCase(), label: present[code].toUpperCase().slice(0, 32),
      priority: 'medium', sound: 'cardiac', repeatSec: PRIORITIES.medium.repeatDefault, maxSoundSec: 0,
    }));
  });

  return active.sort((a, b) => SEVERITY[a.severity] - SEVERITY[b.severity]);
}

/**
 * Signature for change detection. It includes HOW the alarm sounds, not just
 * which alarms are active: otherwise editing the repeat / sound / audible
 * setting of an alarm that is already active (any rule with no delay re-raises
 * in the same evaluation) would never reach the annunciator.
 */
export function alertSignature(alerts) {
  return alerts.map(a => [a.key, a.priority, a.label, a.sound, a.repeatSec, a.maxSoundSec, a.audible !== false, !!a.quiet].join(':')).join('|');
}
